import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import myAxios from "../../../../lib/axios";

const initialState = {
  unreadMessages: [],
  userChats: [],
  users: [],
  loading: false,
  usersLoading: false,
  hasFetchedNotifications: false,
  chatLoading: false,
  error: null,
};

// **1️⃣ Fetch Unread Messages API**
export const fetchUnreadMessages = createAsyncThunk(
  "chat/fetchUnreadMessages",
  async () => {
    try {
      const response = await myAxios.get("/admin/getNotifications");
      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Something went wrong!",
      };
    }
  }
);

// ** Fetch Chats for a Specific User **
export const fetchUserChats = createAsyncThunk(
  "chat/fetchUserChats",
  async ({ userId, isAnonymous }, { rejectWithValue }) => {
    try {
      const response = await myAxios.get(
        `/admin/user-chats?user_id=${userId}&isAnonymous=${isAnonymous}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch user chats"
      );
    }
  }
);

// ** Fetch Users who have done chat **
export const fetchUsers = createAsyncThunk(
  "chat/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await myAxios.get(`/admin/users`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

// **2️⃣ Chat Slice**
const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    clearUnreadMessages: (state) => {
      state.unreadMessages = [];
    },
    clearUserChats: (state) => {
      state.userChats = [];
    },
    clearUsers: (state) => {
      state.users = [];
    },
    addUnreadMessage: (state, action) => {
      state.unreadMessages.push(action.payload);
    },
    markMessageAsRead: (state, action) => {
      state.unreadMessages = state.unreadMessages.filter(
        (msg) => msg.chat_id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnreadMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnreadMessages.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.unreadMessages = action.payload.data;
          state.hasFetchedNotifications = true;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchUnreadMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUserChats.pending, (state) => {
        state.chatLoading = true;
        state.error = null;
      })
      .addCase(fetchUserChats.fulfilled, (state, action) => {
        state.chatLoading = false;
        state.userChats = action.payload;
      })
      .addCase(fetchUserChats.rejected, (state, action) => {
        state.chatLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearUnreadMessages,
  clearUserChats,
  markMessageAsRead,
  addUnreadMessage,
} = chatSlice.actions;
export const chatReducer = chatSlice.reducer;
export const selectUnreadMessages = (state) => state.chat.unreadMessages;
export const selectUserChats = (state) => state.chat.userChats;
export const selectChatLoading = (state) => state.chat.loading;
export const selectUserChatLoading = (state) => state.chat.chatLoading;
export const selectChatError = (state) => state.chat.error;
export const selectHasFetchedNotifications = (state) =>  state.chat.hasFetchedNotifications;
export const selectUsers = (state) => state.chat.users;
export const selectUsersLoading = (state) => state.chat.usersLoading;
